<template>
  <div class="eui-o-pdfviewer__zones"
       :style="canvasStyle"
       @drop="onDrop($event, pageNumber)"
       @dragenter.prevent
       @dragover.prevent>
    <canvas ref="canvas" v-visible.once="drawPage" v-bind="canvasAttrs" @mouseup.self="onMouseUp($event)"></canvas>
    <slot :name="`zone-${pageNumber}`"/>
  </div>
</template>

<script>
import debug from 'debug';

const log = debug('app:_internal/PDFPage');

import {PIXEL_RATIO} from '../utils/constants';
import visible from '../directives/visible';

export default {
  name: 'PDFPage',

  props: {
    page: {
      type: Object, // instance of PDFPageProxy returned from pdf.getPage
      required: true,
    },
    scale: {
      type: Number,
      required: true,
    },
    optimalScale: {
      type: Number,
      required: true,
    },
    isPageFocused: {
      type: Boolean,
      default: false,
    },
    isElementFocused: {
      type: Boolean,
      default: false,
    },
  },

  directives: {
    visible,
  },

  computed: {
    actualSizeViewport() {
      return this.viewport.clone({scale: this.scale});
    },

    canvasStyle() {
      const {width: actualSizeWidth, height: actualSizeHeight} = this.actualSizeViewport;
      const [pixelWidth, pixelHeight] = [actualSizeWidth, actualSizeHeight]
        .map(dim => Math.ceil(dim / PIXEL_RATIO));
      return `width: ${pixelWidth}px; height: ${pixelHeight}px;`;
    },

    canvasAttrs() {
      let {width, height} = this.viewport;
      [width, height] = [width, height].map(dim => Math.ceil(dim));
      const style = this.canvasStyle;

      return {
        width,
        height,
        style,
        class: 'pdf-page box-shadow',
      };
    },

    pageNumber() {
      return this.page.pageNumber;
    },
  },

  methods: {
    onDrop(evt, page) {
      const itemID = evt.dataTransfer.getData('itemID');
      const type = evt.dataTransfer.getData('zoneType');
      const offsetX = parseInt(evt.dataTransfer.getData('offsetX'), 10);
      const offsetY = parseInt(evt.dataTransfer.getData('offsetY'), 10);
      const rect = this.$refs.canvas.getBoundingClientRect();
      const x = Math.round(evt.clientX - rect.left - offsetX);
      const y = Math.round(evt.clientY - rect.top - offsetY);
      this.$emit('dropZone', { id: itemID, page, type, position: { x, y } });
    },
    focusPage() {
      if (this.isPageFocused) return;
      this.$emit('page-focus', this.pageNumber);
    },

    drawPage() {
      if (this.renderTask) return;

      const {viewport} = this;
      const canvasContext = this.$refs.canvas.getContext('2d');
      const renderContext = {canvasContext, viewport};

      // PDFPageProxy#render
      // https://mozilla.github.io/pdf.js/api/draft/PDFPageProxy.html
      this.renderTask = this.page.render(renderContext);
      this.renderTask.promise
        .then(() => {
          this.$emit('page-rendered', {
            page: this.page,
            text: `Rendered page ${this.pageNumber}`,
          });
         })
        .catch(response => {
          this.destroyRenderTask();
          this.$emit('page-errored', {
            response,
            page: this.page,
            text: `Failed to render page ${this.pageNumber}`,
          });
        });
    },

    updateVisibility() {
      this.$parent.$emit('update-visibility');
    },

    destroyPage(page) {
      // PDFPageProxy#_destroy
      // https://mozilla.github.io/pdf.js/api/draft/PDFPageProxy.html
      if (page) page._destroy();

      this.destroyRenderTask();
    },

    destroyRenderTask() {
      if (!this.renderTask) return;

      // RenderTask#cancel
      // https://mozilla.github.io/pdf.js/api/draft/RenderTask.html
      this.renderTask.cancel();
      delete this.renderTask;
    },
    onMouseUp(evt) {
      // get cursor's position
      let rect = evt.target.getBoundingClientRect();
      const payload = {
        x: evt.clientX - rect.left,
        y: evt.clientY - rect.top,
        page: this.page.pageNumber,
      }
      this.$emit('page-click', payload);
    },
  },

  watch: {
    scale: 'updateVisibility',

    page(_newPage, oldPage) {
      this.destroyPage(oldPage);
    },

    isElementFocused(isElementFocused) {
      if (isElementFocused) this.focusPage();
    },
  },

  created() {
    // PDFPageProxy#getViewport
    // https://mozilla.github.io/pdf.js/api/draft/PDFPageProxy.html
    this.viewport = this.page.getViewport({ scale: this.optimalScale });
  },

  mounted() {
    log(`Page ${this.pageNumber} mounted`);
  },

  beforeDestroy() {
    this.destroyPage(this.page);
  },
};
</script>
<style>
.pdf-page {
  display: block;
  margin: auto;
}
.box-shadow {
  /* box-shadow: rgb(0 0 0 / 10%) 0px 5px 10px 0px, rgb(0 0 0 / 30%) 0px 60px 100px -60px;
  box-shadow: 0 15px 30px 0 rgba(0, 0, 0, 0.11), 0 5px 15px 0 rgba(0, 0, 0, 0.08); */
  box-shadow: 0px 7px 4px rgba(0, 0, 0, 0.25);
  /* box-shadow: rgb(0 0 0 / 10%) 0px 5px 10px 0px, rgb(0 0 0 / 30%) 0px 60px 100px -60px; */
}
</style>
